import { useId, type ReactNode } from 'react';
import { Search } from 'lucide-react';
import { SearchableSelect } from '../ui/SearchableSelect';
import type { ConsBusinessFilters, ConsBusinessFilterOptions } from '../../types/consolidatedBusiness';
import { CB_STATUS_META, CB_STATUS_ORDER } from './status';
import { CB_BUSINESS_UNITS } from './constants';

interface Props {
    filters: ConsBusinessFilters;
    options: ConsBusinessFilterOptions | null;
    onChange: (f: ConsBusinessFilters) => void;
}

export function ConsBusinessFiltersBar({ filters, options, onChange }: Props) {
    const set = (patch: Partial<ConsBusinessFilters>) => onChange({ ...filters, ...patch });
    const lostSelected = filters.status === 'Lost';

    const companyOpts = (options?.companies ?? []).map(c => ({ id: c.code, name: c.name }));
    // Cut against the constant, not against whatever the endpoint returns (D4): if a GEN row
    // ever leaks back into the warehouse the picker must not start offering a fourth unit the
    // list cannot show rows for.
    const buOpts = (options?.businessUnits ?? [])
        .filter(b => (CB_BUSINESS_UNITS as readonly string[]).includes(b))
        .map(b => ({ id: b, name: b }));
    const smOpts = nameOpts(options?.salesManagers, filters.salesManager);
    const teamOpts = nameOpts(options?.salesTeams, filters.salesTeam);
    const supplierOpts = nameOpts(options?.supplierNames, filters.supplierName);
    const categoryOpts = nameOpts(options?.categoryNames, filters.categoryName);

    return (
        <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-3">
            {/* Organisation row. Five columns to match the row below, so the two grids line up —
                Sales Team sits next to Sales Manager because it is the same question one level up. */}
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-5">
                <Field label="Company">
                    {id => <SearchableSelect
                        id={id}
                        options={companyOpts}
                        value={filters.companyCode ?? ''}
                        onChange={v => set({ companyCode: v || undefined })}
                        placeholder="Company"
                    />}
                </Field>
                <Field label="Business Unit">
                    {id => <SearchableSelect
                        id={id}
                        options={buOpts}
                        value={filters.businessUnit ?? ''}
                        onChange={v => set({ businessUnit: v || undefined })}
                        placeholder="Business Unit"
                    />}
                </Field>
                <Field label="Sales Team">
                    {id => <SearchableSelect
                        id={id}
                        options={teamOpts}
                        value={filters.salesTeam ?? ''}
                        onChange={v => set({ salesTeam: v || undefined })}
                        placeholder="Sales Team"
                    />}
                </Field>
                <Field label="Sales Manager">
                    {id => <SearchableSelect
                        id={id}
                        options={smOpts}
                        value={filters.salesManager ?? ''}
                        onChange={v => set({ salesManager: v || undefined })}
                        placeholder="Sales Manager"
                    />}
                </Field>
                <Field label="Search">
                    {id => <div className="relative">
                        <Search aria-hidden="true" className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                        <input
                            id={id}
                            type="text"
                            value={filters.search ?? ''}
                            onChange={e => set({ search: e.target.value || undefined })}
                            placeholder="Search customer / product"
                            className="w-full rounded-md border border-gray-300 py-1.5 pl-8 pr-2 text-sm"
                        />
                    </div>}
                </Field>
            </div>

            {/* Supplier / Category + number params */}
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-5">
                <Field label="Supplier">
                    {id => <SearchableSelect
                        id={id}
                        options={supplierOpts}
                        value={filters.supplierName ?? ''}
                        onChange={v => set({ supplierName: v || undefined })}
                        placeholder="Supplier"
                    />}
                </Field>
                <Field label="Subclass">
                    {id => <SearchableSelect
                        id={id}
                        options={categoryOpts}
                        value={filters.categoryName ?? ''}
                        onChange={v => set({ categoryName: v || undefined })}
                        placeholder="Subclass"
                    />}
                </Field>
                <Field label="History months (&lt;)">
                    {id => <NumberInput id={id} value={filters.historyMonths} onChange={v => set({ historyMonths: v })} />}
                </Field>
                <Field label="X months since (&gt;)">
                    {id => <NumberInput id={id} value={filters.monthsSinceLastInvoice} onChange={v => set({ monthsSinceLastInvoice: v })} />}
                </Field>
                <Field label="Min avg qty / year">
                    {id => <NumberInput id={id} value={filters.minSales} onChange={v => set({ minSales: v })} />}
                </Field>
            </div>

            {/* Status pills + the overdue toggle */}
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Status</span>
                <StatusPill
                    label="All"
                    active={!filters.status}
                    onClick={() => set({ status: undefined })}
                />
                {CB_STATUS_ORDER.map(s => (
                    <StatusPill
                        key={s}
                        label={CB_STATUS_META[s].label}
                        active={filters.status === s}
                        onClick={() => set({ status: filters.status === s ? undefined : s })}
                    />
                ))}
                <label className="ml-4 inline-flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                    <input
                        type="checkbox"
                        checked={!!filters.onlyOverdue}
                        onChange={e => set({ onlyOverdue: e.target.checked })}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    Only overdue
                </label>
                {/* Disabled while the Lost status is selected rather than left tickable-but-inert:
                    the two together can only return nothing, and the reason has to be visible on
                    the control the user is reaching for. Picking Lost clears it (reconcileFilters). */}
                <label
                    className={`inline-flex items-center gap-2 text-sm ${
                        lostSelected ? 'cursor-not-allowed text-gray-400' : 'cursor-pointer text-gray-700'
                    }`}
                    title={lostSelected
                        ? 'Not available while the Lost status filter is active'
                        : 'Exclude written-off combinations from the list'}
                >
                    <input
                        type="checkbox"
                        checked={!!filters.hideLost}
                        disabled={lostSelected}
                        onChange={e => set({ hideLost: e.target.checked || undefined })}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                    />
                    Hide lost
                </label>
            </div>
        </div>
    );
}

/**
 * Options for a picker whose value IS its label, with the current selection always among them.
 *
 * The options follow the company picker, so switching company can drop a supplier, subclass or
 * manager that is still selected. SearchableSelect then shows the placeholder while the filter
 * stays active — an empty list with no visible cause. Keeping the selection listed shows why the
 * list is empty and leaves the user one click (the ×) from clearing it; dropping it silently
 * would change the result behind their back.
 */
function nameOpts(values: string[] | undefined, selected: string | undefined) {
    const list = values ?? [];
    const withSelected = selected && !list.includes(selected) ? [selected, ...list] : list;
    return withSelected.map(v => ({ id: v, name: v }));
}

/**
 * A label above EVERY control in both grid rows.
 *
 * Not cosmetic and not optional: SearchableSelect renders only its input, so a row that labels
 * three of five fields lands those three 17px lower than the rest. Removing the three labels
 * fixes the row but loses what the numbers mean; labelling all five is the only version where
 * the fields line up AND still say what they are.
 */
function Field({ label, children }: { label: ReactNode; children: (id: string) => ReactNode }) {
    // The label is tied to its control (design audit S2-05): it was a <label> pointing at
    // nothing, so a screen reader announced the pickers by their placeholder only.
    const id = useId();
    return (
        <div>
            <label htmlFor={id} className="mb-0.5 block text-[11px] font-medium text-gray-600">{label}</label>
            {children(id)}
        </div>
    );
}

function NumberInput({ id, value, onChange }: { id: string; value: string | undefined; onChange: (v: string | undefined) => void }) {
    return (
        <input
            id={id}
            type="number"
            inputMode="numeric"
            value={value ?? ''}
            onChange={e => onChange(e.target.value || undefined)}
            placeholder="—"
            className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
        />
    );
}

/** The visible echo of the stat-card click — one state, two controls, one filter value. */
function StatusPill({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={active}
            className={`rounded-md border px-2.5 py-1 text-xs font-medium ${
                active
                    ? 'border-blue-600 bg-blue-600 text-white'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            }`}
        >
            {label}
        </button>
    );
}
